/**
 * Browser persistence for guided onboarding + completion modal personalisation.
 * Path / dataset / first question / TTV clock are tenant-scoped so multiple test tenants
 * or account switches on one browser do not leak or reuse each other's values.
 *
 * Legacy unscoped keys are still read as fallback; scoped writes clear matching legacy keys
 * where noted to reduce cross-tenant bleed. Logout wipes all onboarding keys via
 * clearOnboardingBrowserStateOnLogout().
 */

const LEGACY_PATH = 'klaris_onboarding_path';
const LEGACY_DATASET = 'klaris_onboarding_dataset';
const LEGACY_FIRST_Q = 'klaris_onboarding_first_q';
const LEGACY_TTV = 'klaris_onboarding_start_ts';

function pathKey(tenantId: string): string {
  return `${LEGACY_PATH}:${tenantId}`;
}
function datasetKey(tenantId: string): string {
  return `${LEGACY_DATASET}:${tenantId}`;
}
function firstQKey(tenantId: string): string {
  return `${LEGACY_FIRST_Q}:${tenantId}`;
}

export function onboardingTtvStorageKey(tenantId: string): string {
  return `klaris_onboarding_ttv_start:${tenantId}`;
}

export function getTenantIdFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return null;
    const t = JSON.parse(raw) as { tenant_id?: string };
    return t?.tenant_id ? String(t.tenant_id) : null;
  } catch {
    return null;
  }
}

function readScopedThenLegacy(tenantId: string | null | undefined, scopedKey: string, legacyKey: string): string | null {
  try {
    if (tenantId) {
      const s = localStorage.getItem(scopedKey);
      if (s) return s;
    }
    return localStorage.getItem(legacyKey);
  } catch {
    return null;
  }
}

export function getOnboardingPath(tenantId: string | null | undefined): string | null {
  const tid = tenantId || undefined;
  if (tid) {
    const v = readScopedThenLegacy(tid, pathKey(tid), LEGACY_PATH);
    return v;
  }
  try {
    return localStorage.getItem(LEGACY_PATH);
  } catch {
    return null;
  }
}

export function setOnboardingPath(tenantId: string | null | undefined, value: string): void {
  try {
    if (tenantId) {
      localStorage.setItem(pathKey(tenantId), value);
      localStorage.removeItem(LEGACY_PATH);
    } else {
      localStorage.setItem(LEGACY_PATH, value);
    }
  } catch {
    /* ignore */
  }
}

export function getOnboardingDataset(tenantId: string | null | undefined): string | null {
  const tid = tenantId || undefined;
  if (tid) {
    return readScopedThenLegacy(tid, datasetKey(tid), LEGACY_DATASET);
  }
  try {
    return localStorage.getItem(LEGACY_DATASET);
  } catch {
    return null;
  }
}

export function setOnboardingDataset(tenantId: string | null | undefined, value: string): void {
  try {
    if (tenantId) {
      localStorage.setItem(datasetKey(tenantId), value);
      localStorage.removeItem(LEGACY_DATASET);
    } else {
      localStorage.setItem(LEGACY_DATASET, value);
    }
  } catch {
    /* ignore */
  }
}

export function removeOnboardingDataset(tenantId: string | null | undefined): void {
  try {
    if (tenantId) localStorage.removeItem(datasetKey(tenantId));
    localStorage.removeItem(LEGACY_DATASET);
  } catch {
    /* ignore */
  }
}

/** Clear path + dataset for this tenant (and legacy globals used by older builds). */
export function removeOnboardingPathAndDataset(tenantId: string | null | undefined): void {
  try {
    if (tenantId) {
      localStorage.removeItem(pathKey(tenantId));
      localStorage.removeItem(datasetKey(tenantId));
    }
    localStorage.removeItem(LEGACY_PATH);
    localStorage.removeItem(LEGACY_DATASET);
  } catch {
    /* ignore */
  }
}

export function getOnboardingFirstQ(tenantId: string | null | undefined): string | null {
  const tid = tenantId || undefined;
  if (tid) {
    return readScopedThenLegacy(tid, firstQKey(tid), LEGACY_FIRST_Q);
  }
  try {
    return localStorage.getItem(LEGACY_FIRST_Q);
  } catch {
    return null;
  }
}

export function setOnboardingFirstQIfEmpty(tenantId: string | null | undefined, text: string): void {
  const t = text.trim();
  if (!t) return;
  try {
    if (tenantId) {
      if (localStorage.getItem(firstQKey(tenantId))) return;
    } else if (localStorage.getItem(LEGACY_FIRST_Q)) {
      return;
    }
    const slice = t.slice(0, 120);
    if (tenantId) {
      localStorage.setItem(firstQKey(tenantId), slice);
      localStorage.removeItem(LEGACY_FIRST_Q);
    } else {
      localStorage.setItem(LEGACY_FIRST_Q, slice);
    }
  } catch {
    /* ignore */
  }
}

/** Start / reset the wall-clock for this tenant (OTP verify + sample dataset pick). */
export function setOnboardingTtvClock(tenantId: string | null | undefined): void {
  if (!tenantId) return;
  try {
    localStorage.setItem(onboardingTtvStorageKey(tenantId), String(Date.now()));
    localStorage.removeItem(LEGACY_TTV);
  } catch {
    /* ignore */
  }
}

/** Milliseconds timestamp for modal elapsed time; 0 if unknown. */
export function getOnboardingTtvStartMs(tenantId: string | null | undefined): number {
  try {
    if (tenantId) {
      const scoped = localStorage.getItem(onboardingTtvStorageKey(tenantId));
      if (scoped) {
        const n = Number(scoped);
        if (n > 0) return n;
      }
    }
    const legacy = Number(localStorage.getItem(LEGACY_TTV) || 0);
    return legacy > 0 ? legacy : 0;
  } catch {
    return 0;
  }
}

/**
 * Call on logout: session id, all wizard/modal onboarding keys (scoped + legacy).
 * Does not remove klaris_onboarding_done_modal_seen:* (already tenant:user scoped).
 */
export function clearOnboardingBrowserStateOnLogout(): void {
  const keysToRemove: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (
        k === 'klaris_onboarding_session_id' ||
        k === LEGACY_PATH ||
        k === LEGACY_DATASET ||
        k === LEGACY_FIRST_Q ||
        k === LEGACY_TTV ||
        k.startsWith(`${LEGACY_PATH}:`) ||
        k.startsWith(`${LEGACY_DATASET}:`) ||
        k.startsWith(`${LEGACY_FIRST_Q}:`) ||
        k.startsWith('klaris_onboarding_ttv_start:')
      ) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
